import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Check } from 'lucide-react';
import { cn } from '../../../lib/utils';

interface CollapsibleSectionProps {
  id: string;
  title: string;
  subtitle?: string;
  completed?: boolean;
  defaultOpen?: boolean;
  children: React.ReactNode;
  className?: string;
  required?: boolean;
}

export function CollapsibleSection({
  id,
  title,
  subtitle,
  completed = false,
  defaultOpen = false,
  children,
  className = '',
  required = false,
}: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const toggleOpen = () => setIsOpen(!isOpen);

  return (
    <div className={cn('border border-gray-200 rounded-lg overflow-hidden', className)}>
      {/* Section Header */}
      <button
        onClick={toggleOpen}
        className="w-full flex items-center justify-between p-4 bg-white hover:bg-gray-50 transition-colors text-left focus:outline-none focus:ring-2 focus:ring-warning focus:ring-offset-2"
        aria-expanded={isOpen}
        aria-controls={`section-${id}`}
      >
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          {/* Completion indicator */}
          <div className={cn(
            'w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0',
            completed 
              ? 'bg-green-100 text-green-600' 
              : required 
                ? 'bg-coral/10 text-coral border-2 border-coral'
                : 'bg-gray-100 text-gray-400'
          )}>
            {completed ? (
              <Check className="w-4 h-4" />
            ) : (
              <span className="text-xs font-semibold">
                {required ? '!' : '?'}
              </span>
            )}
          </div>

          {/* Title and subtitle */}
          <div className="min-w-0 flex-1">
            <h3 className="font-medium text-gray-900 font-header truncate">
              {title}
              {required && <span className="text-warning ml-1">*</span>}
            </h3>
            {subtitle && (
              <p className="text-sm text-gray-600 truncate">{subtitle}</p>
            )}
          </div>

          {/* Status badge */}
          {completed && (
            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full flex-shrink-0">
              Complete
            </span>
          )}
        </div>

        {/* Expand/collapse icon */}
        <div className="ml-3 flex-shrink-0">
          {isOpen ? (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </button>

      {/* Section Content */}
      <div
        id={`section-${id}`}
        className={cn(
          'transition-all duration-200 ease-in-out',
          isOpen 
            ? 'max-h-screen opacity-100' 
            : 'max-h-0 opacity-0 overflow-hidden'
        )}
      >
        <div className="p-4 bg-gray-50 border-t border-gray-200">
          {children}
        </div>
      </div>
    </div>
  );
}